import type { Company, Contact, Lead, PartyChannel } from "../../lib/types";

export type IdentityValueProvenance = {
  source: "company" | "contact" | "lead";
  id: string;
};

export type LeadIdentityContext = {
  company?: Company;
  contacts: Contact[];
  primaryContact?: Contact;
  channels: PartyChannel[];
  displayCompany: string;
  displayCompanySource: IdentityValueProvenance;
  displayContact: string;
  displayContactSource: IdentityValueProvenance;
  displayRole: string;
  displayRoleSource: IdentityValueProvenance;
  displayEmail?: string;
  displayEmailSource?: IdentityValueProvenance;
  displayPhone?: string;
  displayPhoneSource?: IdentityValueProvenance;
  displayInstagram?: string;
  displayInstagramSource?: IdentityValueProvenance;
};

export type IdentitySearchResult = {
  companies: Company[];
  contacts: Array<{ contact: Contact; company: Company }>;
  leads: Array<{ lead: Lead; context: LeadIdentityContext }>;
};

export function protectedLeadIdentityFields(context: LeadIdentityContext): Set<string> {
  const fields = new Set<string>();
  if (context.displayCompanySource.source !== "lead") fields.add("company");
  if (context.displayContactSource.source !== "lead") fields.add("name");
  if (context.displayRoleSource.source !== "lead") fields.add("role");
  if (context.displayEmailSource?.source !== undefined && context.displayEmailSource.source !== "lead") fields.add("email");
  if (context.displayPhoneSource?.source !== undefined && context.displayPhoneSource.source !== "lead") fields.add("phone");
  if (context.displayInstagramSource?.source !== undefined && context.displayInstagramSource.source !== "lead") fields.add("instagram");
  return fields;
}

/**
 * Normaliza texto somente para leitura/busca. Não altera o valor persistido.
 * A busca fica estável entre teclado sem acento, nomes com acento e CNPJ
 * formatado ou compacto.
 */
export function normalizeIdentitySearch(value: string | undefined | null): string {
  return String(value ?? "")
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase()
    .trim()
    .replace(/\s+/g, " ");
}

function compactIdentitySearch(value: string): string {
  return normalizeIdentitySearch(value).replace(/[^a-z0-9]/g, "");
}

export function matchesIdentitySearch(
  values: readonly (string | undefined | null)[],
  query: string,
): boolean {
  const needle = normalizeIdentitySearch(query);
  if (!needle) return true;
  const compactNeedle = compactIdentitySearch(needle);
  return values.some((value) => {
    const haystack = normalizeIdentitySearch(value);
    if (haystack.includes(needle)) return true;
    return Boolean(compactNeedle && compactIdentitySearch(haystack).includes(compactNeedle));
  });
}

function companySearchValues(company: Company): string[] {
  return [
    company.legalName,
    company.tradeName,
    company.cnpj,
    company.normalizedCnpj,
    company.segment,
    company.city,
    company.state,
    company.site,
  ].filter((value): value is string => Boolean(value));
}

function companyHistorySearchValues(companies: Company[], company: Company): string[] {
  return [...companyHistoryIds(companies, company.id)]
    .map((id) => companies.find((item) => item.id === id))
    .filter((item): item is Company => Boolean(item))
    .flatMap(companySearchValues);
}

export function companyMatchesSearch(
  company: Company,
  query: string,
  companies: Company[] = [],
): boolean {
  const isCanonical = companies.some((item) => item.id === company.id);
  return matchesIdentitySearch(
    isCanonical ? companyHistorySearchValues(companies, company) : companySearchValues(company),
    query,
  );
}

function companySlug(value: string): string {
  return normalizeIdentitySearch(value)
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-|-$/g, "") || "empresa";
}

/**
 * Materializa a mesma Company legada que a grade de Companies expõe, sem
 * transformar o Lead antigo em uma linha canônica persistida.
 */
export function legacyCompaniesFromLeads(leads: Lead[]): Company[] {
  const byName = new Map<string, Company>();
  for (const lead of leads) {
    if (lead.companyId || !lead.company.trim()) continue;
    const key = normalizeIdentitySearch(lead.company);
    if (byName.has(key)) continue;
    byName.set(key, {
      id: `legacy-company-${companySlug(lead.company)}`,
      legalName: lead.company,
      tradeName: lead.company,
      site: lead.site,
      segment: lead.segment,
      city: lead.city,
      state: lead.state,
      status: "active",
      createdAt: lead.createdAt,
      updatedAt: lead.createdAt,
    });
  }
  return [...byName.values()];
}

/** Companies visíveis na grade e na busca global: somente ativas + legado. */
export function visibleCompanies(companies: Company[], leads: Lead[]): Company[] {
  const activeCanonical = companies.filter((company) => company.status === "active");
  const canonicalNames = new Set(
    companies
      .flatMap((company) => [company.legalName, company.tradeName])
      .filter(Boolean)
      .map((name) => normalizeIdentitySearch(name)),
  );
  const legacy = legacyCompaniesFromLeads(leads).filter(
    (company) => !canonicalNames.has(normalizeIdentitySearch(company.legalName)),
  );
  return [...activeCanonical, ...legacy];
}

/**
 * Retorna a árvore de aliases que deve aparecer sob uma Company ativa.
 * O merge preserva os company_id históricos; a agregação acontece somente na
 * leitura e segue a cadeia inteira para suportar merges sucessivos.
 */
export function companyHistoryIds(companies: Company[], companyId: string): Set<string> {
  const ids = new Set([companyId]);
  let changed = true;
  while (changed) {
    changed = false;
    for (const company of companies) {
      if (company.status === "merged" && company.mergedIntoId && ids.has(company.mergedIntoId) && !ids.has(company.id)) {
        ids.add(company.id);
        changed = true;
      }
    }
  }
  return ids;
}

/** Resolve um Lead/Contact para a Company ativa no fim da cadeia de merge. */
export function resolveCompanyId(companies: Company[], companyId: string): string {
  let currentId = companyId;
  const visited = new Set<string>();
  while (!visited.has(currentId)) {
    visited.add(currentId);
    const current = companies.find((company) => company.id === currentId);
    if (!current || current.status !== "merged" || !current.mergedIntoId) break;
    const target = companies.find((company) => company.id === current.mergedIntoId);
    if (!target) break;
    currentId = target.id;
  }
  return currentId;
}

export function canonicalCompanyForLead(
  companies: Company[],
  lead: Lead,
): Company | undefined {
  if (!lead.companyId) return undefined;
  const linked = companies.find((company) => company.id === lead.companyId);
  if (!linked) return undefined;
  return companies.find((company) => company.id === resolveCompanyId(companies, linked.id)) ?? linked;
}

export function canonicalCompanyForContact(
  companies: Company[],
  contact: Contact,
): Company | undefined {
  const linked = companies.find((company) => company.id === contact.companyId);
  if (!linked) return undefined;
  return companies.find((company) => company.id === resolveCompanyId(companies, linked.id)) ?? linked;
}

export function contactsForLead(contacts: Contact[], lead: Lead): Contact[] {
  const ids = new Set(
    [...(lead.contactIds ?? []), lead.primaryContactId].filter(
      (id): id is string => Boolean(id),
    ),
  );
  return contacts.filter((contact) => ids.has(contact.id));
}

export function identityContextForLead(
  companies: Company[],
  contacts: Contact[],
  channels: PartyChannel[],
  lead: Lead,
): LeadIdentityContext {
  const company = canonicalCompanyForLead(companies, lead);
  const linkedContacts = contactsForLead(contacts, lead);
  const primaryContact = linkedContacts.find((contact) => contact.id === lead.primaryContactId);
  const displayContactRecord = primaryContact ?? linkedContacts[0];
  const contactIds = new Set(linkedContacts.map((contact) => contact.id));
  const companyIds = company ? companyHistoryIds(companies, company.id) : new Set<string>();
  const linkedChannels = channels.filter(
    (channel) =>
      (channel.contactId && contactIds.has(channel.contactId)) ||
      (!channel.contactId && companyIds.has(channel.companyId)),
  );
  const contactChannelValue = (type: PartyChannel["type"]) => {
    const candidates = linkedChannels.filter(
      (channel) =>
        channel.type === type &&
        channel.status === "active" &&
        Boolean(channel.contactId) &&
        Boolean(channel.value.trim()),
    );
    if (!displayContactRecord) return undefined;
    const selected = candidates
      .filter((channel) => channel.contactId === displayContactRecord.id)
      .find((channel) => channel.primary) ??
      candidates.find((channel) => channel.contactId === displayContactRecord.id);
    return selected
      ? {
          value: selected.value,
          source: { source: "contact" as const, id: displayContactRecord.id },
        }
      : undefined;
  };
  const companyChannelValue = (type: PartyChannel["type"]) => {
    const candidates = linkedChannels.filter(
      (channel) =>
        channel.type === type &&
        channel.status === "active" &&
        !channel.contactId &&
        Boolean(channel.value.trim()),
    );
    const selected = candidates
      .filter((channel) => channel.companyId === company?.id)
      .find((channel) => channel.primary) ??
      candidates.find((channel) => channel.companyId === company?.id) ??
      candidates.find((channel) => channel.primary) ??
      candidates[0];
    return selected
      ? {
          value: selected.value,
          source: { source: "company" as const, id: selected.companyId },
        }
      : undefined;
  };
  const leadValue = (value: string | undefined) => value?.trim()
    ? { value, source: { source: "lead" as const, id: lead.id } }
    : undefined;
  const displayContact = displayContactRecord?.name || lead.name;
  const displayContactSource = displayContactRecord?.name
    ? { source: "contact" as const, id: displayContactRecord.id }
    : { source: "lead" as const, id: lead.id };
  const contactRole = displayContactRecord?.role?.trim();
  const displayRole = contactRole || lead.role;
  const displayRoleSource = contactRole && displayContactRecord
    ? { source: "contact" as const, id: displayContactRecord.id }
    : { source: "lead" as const, id: lead.id };
  const displayCompany = company?.tradeName || company?.legalName || lead.company;
  const displayCompanySource = company
    ? { source: "company" as const, id: company.id }
    : { source: "lead" as const, id: lead.id };
  const email = contactChannelValue("email") ?? leadValue(lead.email) ?? companyChannelValue("email");
  const phone = contactChannelValue("phone") ?? leadValue(lead.phone) ?? companyChannelValue("phone");
  const instagram = contactChannelValue("instagram") ?? leadValue(lead.instagram) ?? companyChannelValue("instagram");
  return {
    company,
    contacts: linkedContacts,
    primaryContact,
    channels: linkedChannels,
    displayCompany,
    displayCompanySource,
    displayContact,
    displayContactSource,
    displayRole,
    displayRoleSource,
    displayEmail: email?.value,
    displayEmailSource: email?.source,
    displayPhone: phone?.value,
    displayPhoneSource: phone?.source,
    displayInstagram: instagram?.value,
    displayInstagramSource: instagram?.source,
  };
}

export function leadSearchValues(
  companies: Company[],
  contacts: Contact[],
  channels: PartyChannel[],
  lead: Lead,
): Array<string | undefined> {
  const context = identityContextForLead(companies, contacts, channels, lead);
  const directCompany = lead.companyId
    ? companies.find((company) => company.id === lead.companyId)
    : undefined;
  const companyHistory = context.company
    ? [...companyHistoryIds(companies, context.company.id)]
      .map((id) => companies.find((company) => company.id === id))
      .filter((company): company is Company => Boolean(company))
    : directCompany
      ? [directCompany]
      : [];
  return [
    lead.name,
    lead.company,
    lead.role,
    lead.email,
    lead.phone,
    lead.instagram,
    lead.segment,
    lead.source,
    lead.city,
    lead.state,
    lead.biggestPain,
    lead.biggestObjection,
    lead.bestOverdelivery,
    ...companyHistory.flatMap(companySearchValues),
    ...context.contacts.flatMap((contact) => [contact.name, contact.role]),
    ...context.channels.flatMap((channel) => [channel.value, channel.normalizedValue]),
  ];
}

export function leadMatchesSearch(
  companies: Company[],
  contacts: Contact[],
  channels: PartyChannel[],
  lead: Lead,
  query: string,
): boolean {
  return matchesIdentitySearch(leadSearchValues(companies, contacts, channels, lead), query);
}

export function searchIdentity(
  companies: Company[],
  contacts: Contact[],
  channels: PartyChannel[],
  leads: Lead[],
): IdentitySearchResult;
export function searchIdentity(
  companies: Company[],
  contacts: Contact[],
  channels: PartyChannel[],
  leads: Lead[],
  query: string,
  limits?: { companies?: number; contacts?: number; leads?: number },
): IdentitySearchResult;
export function searchIdentity(
  companies: Company[],
  contacts: Contact[],
  channels: PartyChannel[],
  leads: Lead[],
  query = "",
  limits: { companies?: number; contacts?: number; leads?: number } = {},
): IdentitySearchResult {
  const visible = visibleCompanies(companies, leads);
  const limit = { companies: 4, contacts: 6, leads: 8, ...limits };
  const matchedContacts = contacts
    .flatMap((contact) => {
      const company = canonicalCompanyForContact(companies, contact);
      if (!company || company.status !== "active") return [];
      const matches = matchesIdentitySearch(
        [contact.name, contact.role, ...companyHistorySearchValues(companies, company), ...channels
          .filter((channel) => channel.contactId === contact.id)
          .flatMap((channel) => [channel.value, channel.normalizedValue])],
        query,
      );
      return matches ? [{ contact, company }] : [];
    })
    .slice(0, limit.contacts);
  const matchedLeads = leads
    .filter((lead) => leadMatchesSearch(companies, contacts, channels, lead, query))
    .map((lead) => ({ lead, context: identityContextForLead(companies, contacts, channels, lead) }))
    .slice(0, limit.leads);
  return {
    companies: visible.filter((company) => companyMatchesSearch(company, query, companies)).slice(0, limit.companies),
    contacts: matchedContacts,
    leads: matchedLeads,
  };
}

export function belongsToCompanyHistory(
  companies: Company[],
  recordCompanyId: string | undefined,
  companyId: string,
): boolean {
  return Boolean(recordCompanyId && companyHistoryIds(companies, companyId).has(recordCompanyId));
}

export function isLeadInCompanyHistory(
  companies: Company[],
  lead: Lead,
  company: Company,
): boolean {
  if (lead.companyId)
    return belongsToCompanyHistory(companies, lead.companyId, company.id);

  // Leads legados sem FK só podem ser associados pelo nome à Company corrente;
  // nunca inventamos um vínculo histórico para um tombstone.
  return company.status === "active" &&
    normalizeIdentitySearch(lead.company) === normalizeIdentitySearch(company.legalName);
}

export function contactsInCompanyHistory(
  companies: Company[],
  contacts: Contact[],
  companyId: string,
): Contact[] {
  const ids = companyHistoryIds(companies, companyId);
  return contacts.filter((contact) => ids.has(contact.companyId));
}

export function channelsInCompanyHistory(
  companies: Company[],
  channels: PartyChannel[],
  companyId: string,
): PartyChannel[] {
  const ids = companyHistoryIds(companies, companyId);
  return channels.filter((channel) => ids.has(channel.companyId));
}

export function leadsInCompanyHistory(
  companies: Company[],
  leads: Lead[],
  company: Company,
): Lead[] {
  return leads.filter((lead) => isLeadInCompanyHistory(companies, lead, company));
}
